import React, { useEffect, useState } from 'react';
import { ShoppingBag, Loader2, Play, Send, CheckCircle, Clock, RefreshCw, Euro, Mail, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cartRemindersAPI, settingsAPI } from '../services/api';

const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white rounded-lg border p-4 flex items-center gap-3" data-testid={testId}>
    <div className="w-10 h-10 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center"><Icon size={18} /></div>
    <div><p className="text-xs text-gray-500">{label}</p><p className="text-lg font-bold text-gray-900">{value}</p></div>
  </div>
);

const AdminCartRemindersPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [hours, setHours] = useState('');

  const load = async () => {
    try { const d = await cartRemindersAPI.overview(); setData(d); setHours(String(d.hours)); } catch { toast.error('Impossible de charger les relances panier'); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async (patch) => {
    setBusy('settings');
    try {
      const current = await settingsAPI.getCompany();
      await settingsAPI.updateCompany({ ...current, ...patch });
      toast.success('Réglages enregistrés');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); } finally { setBusy(''); }
  };

  const runNow = async () => {
    setBusy('run');
    try { const r = await cartRemindersAPI.runNow(); toast.success(r.message); await load(); } catch (e) { toast.error(e.response?.data?.detail || 'Échec'); } finally { setBusy(''); }
  };

  const sendOne = async (userId) => {
    setBusy(userId);
    try { const r = await cartRemindersAPI.send(userId); toast.success(r.message); await load(); } catch (e) { toast.error(e.response?.data?.detail || 'Envoi impossible'); } finally { setBusy(''); }
  };

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>;
  const { stats } = data;
  const rate = stats.total_sent ? Math.round((stats.converted / stats.total_sent) * 100) : 0;

  return (
    <div data-testid="cart-reminders-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ShoppingBag className="text-orange-500" /> Paniers abandonnés</h1>
          <p className="text-sm text-gray-500">Un e-mail de relance est envoyé automatiquement aux clients connectés qui laissent des articles dans leur panier (une seule fois par panier, dans leur langue).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="cart-reminders-refresh"><RefreshCw size={14} className="mr-1" /> Actualiser</Button>
          <Button size="sm" onClick={runNow} disabled={busy === 'run'} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]" data-testid="cart-reminders-run">{busy === 'run' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Play size={14} className="mr-1" />} Envoyer les relances dues ({stats.pending_due})</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat icon={Mail} label="Relances envoyées" value={stats.total_sent} testId="cart-stat-sent" />
        <Stat icon={TrendingUp} label="Paniers récupérés" value={`${stats.converted} (${rate} %)`} testId="cart-stat-converted" />
        <Stat icon={Euro} label="CA récupéré" value={eur(stats.recovered_total)} testId="cart-stat-recovered" />
        <Stat icon={Clock} label="Paniers en attente" value={data.pending.length} testId="cart-stat-pending" />
      </div>

      {stats.followup && (
        <div className="bg-white rounded-lg border p-4 mb-6" data-testid="cart-followup-stats">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Conversion des relances · avec / sans second rappel (-1 % 48 h)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {[['with', 'Avec 2e rappel', 'border-orange-200 bg-orange-50'], ['without', 'Sans 2e rappel', 'border-gray-200 bg-gray-50']].map(([k, label, cls]) => (
              <div key={k} className={`rounded-lg border p-3 ${cls}`} data-testid={`cart-followup-${k}`}>
                <p className="text-xs text-gray-600">{label}</p>
                <p className="text-lg font-bold text-gray-900">{stats.followup[k].rate} % <span className="text-xs font-normal text-gray-500">({stats.followup[k].converted}/{stats.followup[k].sent} récupéré{stats.followup[k].converted > 1 ? 's' : ''})</span></p>
                {k === 'with' && stats.followup.with.sent > 0 && <p className="text-[11px] text-orange-700">dont {stats.followup.with.converted_after_followup} commande(s) passée(s) après le 2e rappel</p>}
              </div>
            ))}
            <div className="rounded-lg border border-gray-200 p-3" data-testid="cart-followup-delta">
              <p className="text-xs text-gray-600">Impact du 2e rappel</p>
              {stats.followup.with.sent && stats.followup.without.sent ? <p className={`text-lg font-bold ${stats.followup.with.rate >= stats.followup.without.rate ? 'text-emerald-700' : 'text-red-600'}`}>{stats.followup.with.rate >= stats.followup.without.rate ? '+' : ''}{stats.followup.with.rate - stats.followup.without.rate} pts</p> : <p className="text-sm text-gray-400">Pas encore assez de données</p>}
              <p className="text-[11px] text-gray-500">{stats.followup.skipped} second(s) rappel(s) non envoyé(s) (panier vidé, commande passée, code utilisé)</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border p-4 mb-6 flex flex-wrap items-center gap-4" data-testid="cart-reminders-settings">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
          <input type="checkbox" checked={data.enabled} disabled={busy === 'settings'} onChange={(e) => saveSettings({ cart_reminder_enabled: e.target.checked })} data-testid="cart-reminders-enabled" className="w-4 h-4 accent-orange-500" />
          Relance automatique {data.enabled ? 'activée' : 'désactivée'}
        </label>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Délai après le dernier ajout :</span>
          <Input type="number" min={1} max={720} value={hours} onChange={(e) => setHours(e.target.value)} className="h-8 w-20 text-sm" data-testid="cart-reminders-hours" />
          <span className="text-gray-600">h</span>
          <Button size="sm" variant="outline" disabled={busy === 'settings' || String(data.hours) === hours} onClick={() => saveSettings({ cart_reminder_hours: Math.min(720, Math.max(1, parseInt(hours, 10) || 24)) })} data-testid="cart-reminders-hours-save">Enregistrer</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="bg-white rounded-lg border overflow-hidden">
          <h2 className="px-4 py-3 border-b font-semibold text-gray-900 text-sm flex items-center gap-2"><Clock size={16} className="text-orange-500" /> Paniers en attente de relance</h2>
          {data.pending.length === 0 ? <p className="p-6 text-sm text-gray-500 text-center" data-testid="cart-pending-empty">Aucun panier en attente.</p> : (
            <div className="overflow-x-auto"><table className="rt-cards w-full text-sm" data-testid="cart-pending-table">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase"><tr><th className="px-3 py-2 text-left">Client</th><th className="px-3 py-2 text-right">Articles</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-left">Relance prévue</th><th className="px-3 py-2"></th></tr></thead>
              <tbody className="divide-y">
                {data.pending.map((p) => (
                  <tr key={p.user_id} data-testid={`cart-pending-${p.user_id}`}>
                    <td data-label="Client" className="px-3 py-2"><p className="font-medium text-gray-900">{p.name || '—'}</p><p className="text-xs text-gray-500">{p.email}</p></td>
                    <td data-label="Articles" className="px-3 py-2 text-right">{p.items}</td>
                    <td data-label="Total" className="px-3 py-2 text-right font-medium">{eur(p.cart_total)}</td>
                    <td data-label="Relance prévue" className="px-3 py-2 text-xs">{p.due ? <span className="text-orange-700 font-medium">Due depuis {fmt(p.due_at)}</span> : <span className="text-gray-500">{fmt(p.due_at)}</span>}</td>
                    <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" disabled={busy === p.user_id} onClick={() => sendOne(p.user_id)} data-testid={`cart-send-${p.user_id}`}>{busy === p.user_id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </section>

        <section className="bg-white rounded-lg border overflow-hidden">
          <h2 className="px-4 py-3 border-b font-semibold text-gray-900 text-sm flex items-center gap-2"><Mail size={16} className="text-orange-500" /> Relances envoyées</h2>
          {data.sent.length === 0 ? <p className="p-6 text-sm text-gray-500 text-center" data-testid="cart-sent-empty">Aucune relance envoyée pour le moment.</p> : (
            <div className="overflow-x-auto"><table className="rt-cards w-full text-sm" data-testid="cart-sent-table">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase"><tr><th className="px-3 py-2 text-left">Client</th><th className="px-3 py-2 text-right">Panier</th><th className="px-3 py-2 text-left">Envoyée</th><th className="px-3 py-2 text-left">Résultat</th></tr></thead>
              <tbody className="divide-y">
                {data.sent.map((r) => (
                  <tr key={r.id} data-testid={`cart-sent-${r.id}`}>
                    <td data-label="Client" className="px-3 py-2"><p className="font-medium text-gray-900">{r.first_name || '—'}</p><p className="text-xs text-gray-500">{r.email} · {r.lang?.toUpperCase()}</p></td>
                    <td data-label="Panier" className="px-3 py-2 text-right">{r.items?.length} art. · {eur(r.cart_total)}</td>
                    <td data-label="Envoyée" className="px-3 py-2 text-xs text-gray-600">{fmt(r.sent_at)}{r.author === 'admin' ? ' · manuel' : ''}</td>
                    <td data-label="Résultat" className="px-3 py-2 text-xs">{r.converted_at ? <span className="inline-flex items-center gap-1 text-green-700 font-medium"><CheckCircle size={12} /> Commande passée {fmt(r.converted_at)}</span> : <span className="text-gray-400">En attente</span>}
                      {r.promo_code && <span className="block text-[11px] text-orange-700 mt-0.5" data-testid={`reminder-promo-${r.id}`}>Code {r.promo_code}{r.followup_sent_at ? (r.followup_skipped ? ` · 2e rappel non envoyé (${r.followup_skipped})` : ` · 2e rappel envoyé ${fmt(r.followup_sent_at)}`) : ' · 2e rappel 6 h avant expiration'}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminCartRemindersPanel;
